/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal functionality for System Misc.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { fetchSystemMiscQuickList } from '../api';
import { SystemMiscConfig } from '../types';

export function useQuickList() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug ?? t('systemMisc.defaultTenant');
  const userId = user?.id;

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListConfigs, setQuickListConfigs] = useState<SystemMiscConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadQuickList = useCallback(async () => {
    try {
      setIsLoading(true);
      const configs = await fetchSystemMiscQuickList(quickListSearch || undefined, tenantSlug, userId ? Number(userId) : undefined);
      setQuickListConfigs(configs);
    } catch {
      setQuickListConfigs([]);
    } finally {
      setIsLoading(false);
    }
  }, [quickListSearch, tenantSlug, userId]);

  useEffect(() => {
    if (showQuickList) {
      void loadQuickList();
    }
  }, [loadQuickList, showQuickList]);

  const handleClose = useCallback(() => {
    setShowQuickList(false);
    setQuickListSearch('');
  }, []);

  const handleSelect = useCallback(
    (config: SystemMiscConfig, onSelect: (config: SystemMiscConfig) => void) => {
      handleClose();
      onSelect(config);
    },
    [handleClose],
  );

  return {
    showQuickList,
    quickListSearch,
    quickListConfigs,
    isLoading,
    setShowQuickList,
    setQuickListSearch,
    handleClose,
    handleSelect,
  };
}

